import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Receipt as TransactionIcon,
  AccountBalance as AccountIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Types
interface Account {
  id: number;
  code: string;
  name: string;
  account_type: string;
  vat_rate: string;
  is_vat_applicable: boolean;
  is_active: boolean;
  full_name: string;
}

interface TransactionEntry {
  id: string;
  account_id: number | null;
  account: Account | null;
  debit_amount: number;
  credit_amount: number;
  description: string;
  vat_rate: string;
  vat_amount: number;
}

interface TransactionData {
  transaction_date: Date;
  description: string;
  reference: string;
  transaction_type: string;
  category: string;
  entries: TransactionEntry[];
}

const TransactionEntry: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState<TransactionData>({
    transaction_date: new Date(),
    description: '',
    reference: '',
    transaction_type: 'expense',
    category: '',
    entries: [
      { id: '1', account_id: null, account: null, debit_amount: 0, credit_amount: 0, description: '', vat_rate: 'standard', vat_amount: 0 },
      { id: '2', account_id: null, account: null, debit_amount: 0, credit_amount: 0, description: '', vat_rate: 'standard', vat_amount: 0 }
    ]
  });

  // Fetch accounts
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await fetch('/api/v1/accounts/');
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts.filter((acc: Account) => acc.is_active));
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to load accounts');
    }
  };

  // Transaction types
  const transactionTypes = [
    { value: 'income', label: 'Income', description: 'Revenue and other income' },
    { value: 'expense', label: 'Expense', description: 'Costs and expenses' },
    { value: 'transfer', label: 'Transfer', description: 'Transfer between accounts' }
  ];

  // Sales Tax rates
  const vatRates = [
    { value: 'standard', label: 'Standard (20%)', percentage: 20 },
    { value: 'reduced', label: 'Reduced (5%)', percentage: 5 },
    { value: 'zero', label: 'Zero (0%)', percentage: 0 },
    { value: 'exempt', label: 'Exempt', percentage: 0 }
  ];

  // Calculate VAT amount
  const calculateVAT = (amount: number, vatRate: string): number => {
    const rates: Record<string, number> = {
      'standard': 0.20,
      'reduced': 0.05,
      'zero': 0.00,
      'exempt': 0.00
    };
    return Math.round(amount * (rates[vatRate] || 0) * 100) / 100;
  };

  // Update transaction field
  const updateTransaction = (field: keyof TransactionData, value: any) => {
    setTransaction(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update entry field
  const updateEntry = (entryId: string, field: keyof TransactionEntry, value: any) => {
    setTransaction(prev => ({
      ...prev,
      entries: prev.entries.map(entry => {
        if (entry.id === entryId) {
          const updatedEntry = { ...entry, [field]: value };

          // Auto-calculate VAT when amount or rate changes
          if (field === 'debit_amount' || field === 'credit_amount' || field === 'vat_rate') {
            const amount = field === 'debit_amount' ? value : (field === 'credit_amount' ? value : (updatedEntry.debit_amount || updatedEntry.credit_amount));
            const vatRate = field === 'vat_rate' ? value : updatedEntry.vat_rate;

            if (updatedEntry.account?.is_vat_applicable) {
              updatedEntry.vat_amount = calculateVAT(amount, vatRate);
            } else {
              updatedEntry.vat_amount = 0;
            }
          }

          // Set account details when account is selected
          if (field === 'account_id') {
            const selectedAccount = accounts.find(acc => acc.id === value);
            updatedEntry.account = selectedAccount || null;
            if (selectedAccount) {
              updatedEntry.vat_rate = selectedAccount.vat_rate;
              updatedEntry.description = updatedEntry.description || prev.description;
            }
          }

          return updatedEntry;
        }
        return entry;
      })
    }));
  };

  // Add new entry
  const addEntry = () => {
    const newEntry: TransactionEntry = {
      id: Date.now().toString(),
      account_id: null,
      account: null,
      debit_amount: 0,
      credit_amount: 0,
      description: transaction.description,
      vat_rate: 'standard',
      vat_amount: 0
    };

    setTransaction(prev => ({
      ...prev,
      entries: [...prev.entries, newEntry]
    }));
  };

  // Remove entry
  const removeEntry = (entryId: string) => {
    if (transaction.entries.length <= 2) {
      toast.error('At least 2 entries are required for double-entry bookkeeping');
      return;
    }

    setTransaction(prev => ({
      ...prev,
      entries: prev.entries.filter(entry => entry.id !== entryId)
    }));
  };

  // Calculate totals
  const totalDebits = transaction.entries.reduce((sum, entry) => sum + (entry.debit_amount || 0), 0);
  const totalCredits = transaction.entries.reduce((sum, entry) => sum + (entry.credit_amount || 0), 0);
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;
  const totalVAT = transaction.entries.reduce((sum, entry) => sum + (entry.vat_amount || 0), 0);

  // Validate transaction
  const validateTransaction = (): string[] => {
    const errors: string[] = [];

    if (!transaction.description.trim()) {
      errors.push('Transaction description is required');
    }

    if (transaction.entries.length < 2) {
      errors.push('At least 2 entries are required');
    }

    if (!isBalanced) {
      errors.push(`Debits ($${totalDebits.toFixed(2)}) must equal credits ($${totalCredits.toFixed(2)})`);
    }

    transaction.entries.forEach((entry, index) => {
      if (!entry.account_id) {
        errors.push(`Entry ${index + 1}: Account is required`);
      }

      if (entry.debit_amount === 0 && entry.credit_amount === 0) {
        errors.push(`Entry ${index + 1}: Either debit or credit amount is required`);
      }

      if (entry.debit_amount > 0 && entry.credit_amount > 0) {
        errors.push(`Entry ${index + 1}: Cannot have both debit and credit amounts`);
      }
    });

    return errors;
  };

  // Submit transaction
  const submitTransaction = async () => {
    const errors = validateTransaction();
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    setLoading(true);
    try {
      const transactionData = {
        transaction_date: transaction.transaction_date.toISOString().split('T')[0],
        description: transaction.description,
        reference: transaction.reference || undefined,
        transaction_type: transaction.transaction_type,
        category: transaction.category || undefined,
        entries: transaction.entries.map(entry => ({
          account_id: entry.account_id,
          debit_amount: entry.debit_amount || 0,
          credit_amount: entry.credit_amount || 0,
          description: entry.description || transaction.description,
          vat_rate: entry.account?.is_vat_applicable ? entry.vat_rate : undefined,
          vat_amount: entry.vat_amount || 0
        }))
      };

      const response = await fetch('/api/v1/transactions/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      toast.success(`Transaction created successfully! Reference: ${result.reference}`);

      // Reset form
      setTransaction({
        transaction_date: new Date(),
        description: '',
        reference: '',
        transaction_type: 'expense',
        category: '',
        entries: [
          { id: '1', account_id: null, account: null, debit_amount: 0, credit_amount: 0, description: '', vat_rate: 'standard', vat_amount: 0 },
          { id: '2', account_id: null, account: null, debit_amount: 0, credit_amount: 0, description: '', vat_rate: 'standard', vat_amount: 0 }
        ]
      });

    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            <TransactionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Transaction Entry
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={submitTransaction}
            disabled={loading || !isBalanced}
            color={isBalanced ? "primary" : "error"}
          >
            {loading ? 'Saving...' : 'Save Transaction'}
          </Button>
        </Box>

        {/* Balance Status */}
        <Card sx={{ mb: 3, bgcolor: isBalanced ? 'success.light' : 'error.light' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="h6" color={isBalanced ? 'success.dark' : 'error.dark'}>
                  ${totalDebits.toFixed(2)} Debits
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h6" color={isBalanced ? 'success.dark' : 'error.dark'}>
                  ${totalCredits.toFixed(2)} Credits
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h6" color={isBalanced ? 'success.dark' : 'error.dark'}>
                  ${totalVAT.toFixed(2)} VAT
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Chip
                  label={isBalanced ? "BALANCED" : "OUT OF BALANCE"}
                  color={isBalanced ? "success" : "error"}
                  size="medium"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Transaction Details */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Transaction Details
          </Typography>

          <Grid container spacing={3}>
            {/* Date */}
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Transaction Date"
                value={transaction.transaction_date}
                onChange={(date) => updateTransaction('transaction_date', date || new Date())}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </Grid>

            {/* Type */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={transaction.transaction_type}
                  label="Transaction Type"
                  onChange={(e) => updateTransaction('transaction_type', e.target.value)}
                >
                  {transactionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box>
                        <Typography variant="body1">{type.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Reference */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Reference (Optional)"
                value={transaction.reference}
                onChange={(e) => updateTransaction('reference', e.target.value)}
                placeholder="INV-001, CHQ-123, etc."
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Category (Optional)"
                value={transaction.category}
                onChange={(e) => updateTransaction('category', e.target.value)}
                placeholder="Office Supplies, Travel, etc."
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transaction Description"
                value={transaction.description}
                onChange={(e) => updateTransaction('description', e.target.value)}
                placeholder="Describe the transaction..."
                required
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Journal Entries */}
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Journal Entries
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addEntry}
            >
              Add Entry
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Debit ($)</TableCell>
                  <TableCell align="right">Credit ($)</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Sales Tax Rate</TableCell>
                  <TableCell align="right">Sales Tax ($)</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.entries.map((entry, index) => (
                  <TableRow key={entry.id}>
                    {/* Account Selection */}
                    <TableCell sx={{ minWidth: 250 }}>
                      <Autocomplete
                        options={accounts}
                        getOptionLabel={(option) => option.full_name}
                        value={entry.account}
                        onChange={(_, value) => updateEntry(entry.id, 'account_id', value?.id || null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select account..."
                            size="small"
                            error={!entry.account_id}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body2">
                                {option.code} - {option.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.account_type} • VAT: {option.vat_rate}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                    </TableCell>

                    {/* Debit Amount */}
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={entry.debit_amount || ''}
                        onChange={(e) => updateEntry(entry.id, 'debit_amount', parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                        disabled={entry.credit_amount > 0}
                        sx={{ width: 120 }}
                      />
                    </TableCell>

                    {/* Credit Amount */}
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={entry.credit_amount || ''}
                        onChange={(e) => updateEntry(entry.id, 'credit_amount', parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                        disabled={entry.debit_amount > 0}
                        sx={{ width: 120 }}
                      />
                    </TableCell>

                    {/* Description */}
                    <TableCell>
                      <TextField
                        size="small"
                        value={entry.description}
                        onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                        placeholder="Entry description..."
                        sx={{ minWidth: 150 }}
                      />
                    </TableCell>

                    {/* Sales Tax Rate */}
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={entry.vat_rate}
                          onChange={(e) => updateEntry(entry.id, 'vat_rate', e.target.value)}
                          disabled={!entry.account?.is_vat_applicable}
                        >
                          {vatRates.map((rate) => (
                            <MenuItem key={rate.value} value={rate.value}>
                              {rate.percentage}%
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Sales Tax Amount */}
                    <TableCell align="right">
                      <Typography variant="body2" color={entry.vat_amount > 0 ? "primary" : "text.secondary"}>
                        ${entry.vat_amount.toFixed(2)}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeEntry(entry.id)}
                        disabled={transaction.entries.length <= 2}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Validation Errors */}
          {!isBalanced && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Transaction is out of balance:</strong>
              </Typography>
              <Typography variant="body2">
                Debits: ${totalDebits.toFixed(2)} • Credits: ${totalCredits.toFixed(2)} •
                Difference: ${Math.abs(totalDebits - totalCredits).toFixed(2)}
              </Typography>
            </Alert>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default TransactionEntry;
